<template>
  <component :is="$route.meta.layout || 'default-layout'">
    <router-view></router-view>
    <!-- Rendered inside the layout so it sits within v-app; Vuetify detaches the
         dialog to [data-app] itself. Never mounted on the unauthenticated shell. -->
    <feedback-prompt-dialog v-if="isAuthenticated" />
    <!-- Asks what to do with uploads whose names already exist in the facility or group. -->
    <document-name-conflicts-dialog v-if="isAuthenticated" />
    <!-- Background "Add Document" uploads; stays across routes so the user can keep working. -->
    <document-upload-tray v-if="isAuthenticated" />
  </component>
</template>

<script>
  //import defaultLayout from "@/layouts/default.vue";
  import authLayout from '@Layouts/auth.vue';
  import defaultLayout from '@Layouts/default.vue';
  import adminLayout from '@Layouts/admin.vue';
  import viewerLayout from '@Layouts/view.vue';
  import accountLayout from '@Layouts/account.vue';
  import potreeLayout from '@Layouts/potree.vue';
  import { $getFromLocal, $has } from './utils';
  import permissions from '@Share/config/permissions';
  import FeedbackPromptDialog from '@Feedback/components/FeedbackPromptDialog.vue';
  import DocumentNameConflictsDialog from '@/features/Documents/components/DocumentNameConflictsDialog.vue';
  import DocumentUploadTray from '@/features/Documents/components/DocumentUploadTray.vue';

  /** Layouts the Feedback Prompt defers past — see isImmersiveRoute. */
  const IMMERSIVE_LAYOUTS = ['viewer-layout', 'potree-layout'];

  export default {
    name: 'App',
    created() {
      this.$vuetify.theme.dark = /true/.test($getFromLocal('isDarkMode', false, false));
    },
    mounted() {
      // Refresh cached user profile on every app mount so any stale avatarUrl
      // (e.g. from a login response that bypassed URL signing, or a session older
      // than a SAS token's lifetime) is replaced with a freshly-signed one.
      // Dispatched only when authenticated; the action itself is a no-op otherwise.
      if (this.$store.getters['ACCOUNT/GET_IS_AUTH']) {
        this.$store.dispatch('ACCOUNT/REFRESH_CURRENT_USER');
      }
    },
    watch: {
      // Feedback Prompt (AVA-349) session-start trigger. A watcher, not mounted():
      // credential login reloads the page (account.modules.js LOGIN sets
      // location.href) and a token-bearing app load hits this immediately, but the
      // SSO callback only calls $router.replace('/') (SSOCallback.vue:93), which
      // never remounts App - a mounted() hook would silently skip every SSO user.
      // The store's sessionStorage guard keeps this to one prompt per session.
      isAuthenticated: {
        immediate: true,
        handler() {
          this.maybePromptForFeedback();
        },
      },
      $route: {
        deep: true,
        immediate: true,
        handler(route) {
          if (route.params.id && route.params.id != this.$store.state.assetId) {
            this.$store.state.assetId = route.params.id;
            this.$store.dispatch('FACILITY/GET_BY_ID', {
              id: route.params.id,
              selectFacility: true,
            });
            this.$store.dispatch('FACILITY/ADD_TO_RECENT', route.params.id);

            // Permission-gated dispatches - only call APIs user has permission to access
            if ($has(permissions.TaskTracker.View)) {
              this.$store.dispatch('TASK_MANAGER/GET');
              this.$store.dispatch('TASK_MANAGER/GET_BY_USER');
            }
            if ($has(permissions.Registers.View)) {
              this.$store.dispatch('REGISTERS/FETCH_USER_FACILITY_REGISTERS');
            }
          }

          // A prompt deferred past an immersive route gets its chance as soon as
          // the user navigates somewhere it is not an interruption.
          this.maybePromptForFeedback();
        },
      },
    },
    methods: {
      /**
       * Ask the server whether to show the Feedback Prompt (AVA-349), unless the
       * user is on a surface where it would land over live work. The store owns
       * the once-per-session guard and does not burn it on a deferred check, so
       * deferring costs the user nothing.
       */
      maybePromptForFeedback() {
        if (!this.isAuthenticated || this.isImmersiveRoute) return;
        this.$store.dispatch('FEEDBACK/CHECK_ELIGIBILITY');
      },
    },
    components: {
      authLayout,
      defaultLayout,
      adminLayout,
      viewerLayout,
      accountLayout,
      potreeLayout,
      FeedbackPromptDialog,
      DocumentNameConflictsDialog,
      DocumentUploadTray,
    },
    computed: {
      isloggin: {
        get() {
          return this.$store.getters['ACCOUNT/isLogin'];
        },
      },
      isAuthenticated() {
        return this.$store.getters['ACCOUNT/GET_IS_AUTH'];
      },
      /**
       * Surfaces where a reload restores heavy in-progress work — a loaded model,
       * a section box, an unsaved measurement. "App load with an existing token"
       * counts as a session start (spec AVA-349), so an F5 mid-inspection would
       * otherwise put a scrim over live work.
       */
      isImmersiveRoute() {
        return IMMERSIVE_LAYOUTS.includes(this.$route.meta.layout);
      },
    },
  };
</script>
<style lang="scss">
  .app-container::-webkit-scrollbar-thumb {
    display: none;
  }
  $font-family: 'IBM Plex Sans';

  .v-application {
    [class*='text-'] {
      font-family: $font-family, 'Roboto', sans-serif !important;
    }
    font-family: $font-family, 'Roboto', sans-serif !important;
  }

  .theme--dark.v-sheet {
    background-color: var(--v-bg2-base);
  }
  .theme--dark.v-data-table {
    background-color: var(--v-bg2-base);
    border-color: var(--v-bg2-base);
  }
  .fill-height {
    height: 100%;
  }
  .flex-1 {
    flex: 1;
  }
  .table-sticky {
    overflow: hidden;

    .v-data-table__wrapper {
      overflow: visible;
      overflow: auto;
      overflow: overlay;
      height: 100%;
      flex: 1 1 auto;
      border-radius: 4px 4px 0 0;
      /* background: var(--v-bg1-base); */
      border: 1px solid rgba(0, 0, 0, 0.1); /* Light mode */

      .theme--dark & {
        border: 1px solid rgba(255, 255, 255, 0.1); /* Dark mode */
      }

      .v-data-table-header {
        position: sticky;
        z-index: 6;
        top: 0;
        background: var(--v-bg2-base);
        border: 1px solid rgba(0, 0, 0, 0.1); /* Light mode */
        height: 44px;

        .theme--dark & {
          border: 1px solid rgba(255, 255, 255, 0.1); /* Dark mode */
        }
      }
    }

    .v-data-footer {
      justify-content: flex-end;
      gap: 2rem;
      .v-data-footer__pagination {
        margin: 0;
      }
      .v-data-footer__icons-before .v-btn--icon.v-size--default,
      .v-data-footer__icons-after .v-btn--icon.v-size--default {
        width: 25px;
        height: 25px;
      }
      .v-data-footer__select {
        order: 0;
        margin: 0;
        text-align: right;
        display: flex;
        flex-direction: row-reverse;

        .v-input {
          margin: 0;
          margin-right: 0.5rem;
        }
        .v-select__selection {
          font-size: 12px !important;
        }

        .v-input__append-inner {
          padding: 0;
          width: 8px;
          min-width: 8px;

          .v-input__icon {
            width: 8px;
            min-width: 8px;
          }
        }
      }
      .v-data-footer__icons-before {
        order: 1;
      }
      .v-data-footer__pagination {
        order: 2;
      }
      .v-data-footer__icons-after {
        order: 3;
      }
    }
  }
  .custom-table {
    .v-data-footer {
      background: var(--v-bg1-base);
      border: 1px solid rgba(0, 0, 0, 0.1); /* Light mode */
      border-radius: 0 0 4px 4px;
      padding: 0 4px;
      font-size: 14px;
      position: sticky;
      bottom: 0;
      z-index: 10;

      .theme--dark & {
        border: 1px solid rgba(255, 255, 255, 0.1); /* Dark mode */
      }
    }
    .v-data-footer__select .v-select {
      margin: 0 0 0 34px;
    }

    &.v-data-table {
      overflow: overlay;
      height: -webkit-fill-available;
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;

      .v-data-table__wrapper {
        overflow: overlay;
        height: 100%;
      }
    }

    &.v-data-table > .v-data-table__wrapper > table tr {
      th,
      td {
        padding: 0 6px;
      }
    }

    tr {
      position: relative;
    }
    .v-data-table-header {
      background-color: var(--v-bg2-base);
    }
    &.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
      height: 28px;
    }
    &.v-data-table > .v-data-table__wrapper {
      background-color: rgb(0 0 0 / 4%);
      .theme--dark & {
        background-color: rgb(255 255 255 / 4%);
      }
      .v-input--checkbox .v-icon.v-icon {
        font-size: 20px;
      }
    }
    .v-data-footer {
      height: 33px;
      background-color: var(--v-bg2-base);
      .theme--dark.v-text-field > .v-input__control > .v-input__slot:before {
        display: none;
      }
    }
    .v-data-table__checkbox .v-icon.v-icon {
      font-size: 20px;
    }
  }
  .v-application.theme--dark {
    .gantt_container {
      background-color: var(--v-bg2-base);
    }
    .vue-slideout-layout {
      background-color: var(--v-bg1-base) !important;
      .vue-slideout-header {
        border-bottom-color: #000;
        border-top-color: #000;
      }
      .vue-slideout-footer {
        border-top-color: #000;
      }
    }
  }
  .v-input.search-bar {
    border-radius: 4px;
    .v-input__slot {
      min-height: 36px !important;
    }
  }
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    -webkit-background-clip: text;
    -webkit-text-fill-color: var(--v-black-base);
    -webkit-transition: background-color 5000s ease-in-out 0s;
    transition: background-color 5000s ease-in-out 0s;
    box-shadow: inset 0 0 20px 20px #23232300;
  }
  .v-text-field.v-input--is-readonly {
    &.v-text-field--outlined fieldset {
      border-style: dashed !important;
    }

    .v-input__append-inner {
      display: none !important;
    }

    .v-input__control > .v-input__slot:hover {
      background-color: transparent !important;
    }
  }
  .bg1.opened {
    border-left: 3px solid var(--v-primary-base) !important;
  }

  .top-selection-check {
    position: absolute;
    top: 5px;
    left: 5px;
  }

  .v-main__wrap {
    height: calc(100vh - var(--ds-nav-top-bar-height));
  }
</style>
